/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ULD.OVERRIDES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UldOverridesPage.tsx. That desktop
 * screen lets an admin pick a page + workspace and hand-edit the layout DELTA
 * (an ordered array of declarative mutations keyed by component_key) plus review
 * collateral-damage telemetry (delta actions whose component_key the current
 * release no longer ships). This file is a DISTINCT presentation (dense card
 * list + facet chips + KPI summary + bottom-sheet detail) over the SAME
 * data/logic layer only:
 *   - reads via apiDb (uld_layout_delta + uld_apply_log), resolving each delta's
 *     page_code to the page's translated name against the ACTIVE locale
 *     (csm_page_master + _tl; §8, §17.B4 — never a raw id/code reaches render),
 *   - the activate/deactivate write routes through the Common Routine
 *     (routedUpdate) — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop editor; §16. Editing the delta JSON itself
 * stays on the desktop (like MobileUserEditSheet keeps role editing there); the
 * mobile screen surfaces and toggles a delta, showing its actions read-only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronRight, Inbox, Layers, Globe, Building2, Power, X, TriangleAlert } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ULD.OVERRIDES';

// ---------------------------------------------------------------------------
// Delta-action vocabulary (matches the desktop ACTIONS/VALUE_HINT). CHECK-enum
// style labels go through i18n via a labelled constant — never a bare literal
// scattered in JSX (§17.B11).
// ---------------------------------------------------------------------------

const ACTION_LABELS: { value: string; labelKey: string; def: string }[] = [
  { value: 'hide_element', labelKey: 'mobile.uld.action.hide_element', def: 'Hide element' },
  { value: 'reorder_index', labelKey: 'mobile.uld.action.reorder_index', def: 'Reorder' },
  { value: 'relabel_component', labelKey: 'mobile.uld.action.relabel_component', def: 'Relabel' },
  { value: 'assign_field_to_tab', labelKey: 'mobile.uld.action.assign_field_to_tab', def: 'Assign to tab' },
  { value: 'set_section', labelKey: 'mobile.uld.action.set_section', def: 'Set section' },
  { value: 'set_col_span', labelKey: 'mobile.uld.action.set_col_span', def: 'Set column span' },
];

interface DeltaAction { action: string; component_key: string; value: string }

interface MobileUldRow {
  pk: number;
  workspace_code: string;
  page_code: string;
  page_name: string;
  is_active: 'Y' | 'N';
  updated_at: string;
  actions: DeltaAction[];
}

interface StaleRow {
  id: number;
  workspace_code: string;
  page_code: string;
  component_key: string;
  action_type: string;
  reason: string;
  occurrence_count: number;
  last_seen_at: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';
interface TlRow { locale_code: string; display_text: string }

/** Pull the human-facing value out of a raw delta-action object (mirrors the
 *  desktop actionToRow value extraction — the field name depends on the action). */
function actionValue(a: Record<string, unknown>): string {
  switch (String(a.action ?? '')) {
    case 'reorder_index': return String(a.index ?? '');
    case 'relabel_component': return String(a.label ?? '');
    case 'assign_field_to_tab': return String(a.tab ?? '');
    case 'set_section': return String(a.section ?? '');
    case 'set_col_span': return String(a.col_span ?? '');
    default: return '';
  }
}

function parseDelta(delta: unknown): DeltaAction[] {
  if (!Array.isArray(delta)) return [];
  return (delta as Record<string, unknown>[]).map((a) => ({
    action: String(a.action ?? ''),
    component_key: String(a.component_key ?? ''),
    value: actionValue(a),
  }));
}

/** Group key that ties a delta to its collateral-damage telemetry (the
 *  uld_apply_log unique-ish scope is workspace_code + page_code). */
const scopeKey = (workspace_code: string, page_code: string) => `${workspace_code}\x00${page_code}`;

export function MobileUldOverridesPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileUldRow[]>([]);
  const [staleByScope, setStaleByScope] = useState<Record<string, StaleRow[]>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [selected, setSelected] = useState<MobileUldRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [deltaRes, logRes, pageRes] = await Promise.all([
        apiDb
          .from('uld_layout_delta')
          .select('uld_layout_delta_id, workspace_code, page_code, delta, is_active, updated_at')
          .order('page_code'),
        apiDb
          .from('uld_apply_log')
          .select('uld_apply_log_id, workspace_code, page_code, component_key, action_type, reason, occurrence_count, last_seen_at')
          .order('last_seen_at', { ascending: false }),
        apiDb
          .from('csm_page_master')
          .select('internal_code, csm_page_master_tl ( locale_code, display_text )'),
      ]);
      if (deltaRes.error) throw deltaRes.error;
      if (logRes.error) throw logRes.error;
      if (pageRes.error) throw pageRes.error;

      // Resolve page_code (internal_code) -> translated page name (§17.B4).
      type RawPage = { internal_code: string; csm_page_master_tl?: TlRow[] };
      const pageNames: Record<string, string> = {};
      for (const p of (pageRes.data ?? []) as RawPage[]) {
        const tls = p.csm_page_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        pageNames[p.internal_code] = exact?.display_text ?? fb?.display_text ?? p.internal_code;
      }

      type RawDelta = {
        uld_layout_delta_id: number;
        workspace_code: string;
        page_code: string;
        delta: unknown;
        is_active: 'Y' | 'N';
        updated_at: string;
      };
      const list: MobileUldRow[] = ((deltaRes.data ?? []) as RawDelta[]).map((d) => ({
        pk: d.uld_layout_delta_id,
        workspace_code: d.workspace_code ?? '',
        page_code: d.page_code,
        page_name: pageNames[d.page_code] ?? d.page_code,
        is_active: d.is_active,
        updated_at: d.updated_at,
        actions: parseDelta(d.delta),
      }));
      setRows(list);

      type RawLog = {
        uld_apply_log_id: number;
        workspace_code: string;
        page_code: string;
        component_key: string;
        action_type: string;
        reason: string;
        occurrence_count: number;
        last_seen_at: string;
      };
      const grouped: Record<string, StaleRow[]> = {};
      for (const w of (logRes.data ?? []) as RawLog[]) {
        const ws = w.workspace_code ?? '';
        const key = scopeKey(ws, w.page_code);
        const entry: StaleRow = {
          id: w.uld_apply_log_id,
          workspace_code: ws,
          page_code: w.page_code,
          component_key: w.component_key,
          action_type: w.action_type,
          reason: w.reason,
          occurrence_count: w.occurrence_count,
          last_seen_at: w.last_seen_at,
        };
        (grouped[key] ??= []).push(entry);
      }
      setStaleByScope(grouped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (page names follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const staleFor = useCallback(
    (r: { workspace_code: string; page_code: string }): StaleRow[] =>
      staleByScope[scopeKey(r.workspace_code, r.page_code)] ?? [],
    [staleByScope],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (warningsOnly && staleFor(r).length === 0) return false;
      if (!q) return true;
      return (
        r.page_name.toLowerCase().includes(q) ||
        r.page_code.toLowerCase().includes(q) ||
        r.workspace_code.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, warningsOnly, staleFor]);

  const kpiDeltas = rows.length;
  const kpiWarnings = useMemo(
    () => rows.reduce((n, r) => n + staleFor(r).length, 0),
    [rows, staleFor],
  );

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.uld.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const actionLabel = (value: string): string => {
    const m = ACTION_LABELS.find((a) => a.value === value);
    return m ? t(m.labelKey, m.def) : value;
  };

  const scopeLabel = (r: { workspace_code: string }): string =>
    r.workspace_code ? r.workspace_code : t('mobile.uld.global', 'Global');

  const actionsLabel = (n: number) => t('mobile.uld.actions_count', { count: n, defaultValue: `${n} actions` });
  const warningsLabel = (n: number) => t('mobile.uld.warnings_count', { count: n, defaultValue: `${n} warnings` });

  const toggleActive = async (r: MobileUldRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'uld_layout_delta', 'uld_layout_delta_id', r.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const selectedStale = selected ? staleFor(selected) : [];

  return (
    <section className="m-screen">
      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.uld.search', 'Search layout overrides')}
          aria-label={t('mobile.uld.search', 'Search layout overrides')}
        />
      </div>

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.uld.kpi_deltas', 'Layout deltas')}</div>
          <div className="m-kpi-v">{kpiDeltas.toLocaleString()}</div>
        </div>
        <div className={'m-kpi' + (kpiWarnings > 0 ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.uld.kpi_warnings', 'Stale keys')}</div>
          <div className="m-kpi-v">{kpiWarnings.toLocaleString()}</div>
        </div>
      </div>

      <div className="m-chips">
        {statusFacets.map((f) => (
          <button
            key={f.key}
            type="button"
            className={'m-chip' + (statusFacet === f.key ? ' m-active' : '')}
            onClick={() => setStatusFacet(f.key)}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          className={'m-chip' + (warningsOnly ? ' m-active' : '')}
          onClick={() => setWarningsOnly((v) => !v)}
        >
          {t('mobile.uld.warnings_only', 'Stale keys')}
        </button>
      </div>

      {error && (
        <div className="m-card">
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </div>
      )}

      {filtered.length === 0 && !busy ? (
        <div className="m-card"><div className="m-empty"><Inbox /> {t('mobile.uld.none', 'No layout overrides match')}</div></div>
      ) : (
        <div className="m-card"><div className="m-list">
          {filtered.map((r) => {
            const stale = staleFor(r);
            const leadClass = stale.length > 0 ? 'm-l-amber' : r.is_active === 'Y' ? 'm-l-blue' : 'm-l-slate';
            return (
              <button key={r.pk} type="button" className="m-row" onClick={() => setSelected(r)}>
                <span className={'m-lead ' + leadClass} aria-hidden>
                  <Layers />
                </span>
                <span className="m-body">
                  <span className="m-h">{r.page_name}</span>
                  <span className="m-s">
                    {scopeLabel(r)} · {actionsLabel(r.actions.length)}
                    {stale.length > 0 ? ' · ' + warningsLabel(stale.length) : ''}
                  </span>
                </span>
                <span className={'m-pill ' + (r.is_active === 'Y' ? 'm-p-ok' : 'm-p-no')}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </span>
                <span className="m-chev"><ChevronRight /></span>
              </button>
            );
          })}
        </div></div>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.uld.detail', 'Layout override detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Layers size={20} /></span>
              <span>
                <div className="m-nm">{selected.page_name}</div>
                <div className="m-em">{selected.page_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-field"><span className="m-k">{t('mobile.uld.scope', 'Scope')}</span>
                <span className="m-v">
                  {selected.workspace_code
                    ? <><Building2 size={13} style={{ verticalAlign: '-2px' }} /> {selected.workspace_code}</>
                    : <><Globe size={13} style={{ verticalAlign: '-2px' }} /> {t('mobile.uld.global', 'Global')}</>}
                </span>
              </div>
              <div className="m-field"><span className="m-k">{t('mobile.uld.status', 'Status')}</span>
                <span className="m-v">
                  <span className={'m-pill ' + (selected.is_active === 'Y' ? 'm-p-ok' : 'm-p-no')}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </span>
                </span>
              </div>
              <div className="m-field"><span className="m-k">{t('mobile.uld.actions', 'Delta actions')}</span>
                <span className="m-v">{actionsLabel(selected.actions.length)}</span>
              </div>
              <div className="m-field"><span className="m-k">{t('mobile.uld.updated', 'Updated')}</span>
                <span className="m-v">{formatDateTime(selected.updated_at)}</span>
              </div>
            </div>

            {/* Delta actions — read-only; editing the JSON stays on the desktop. */}
            <div className="m-fieldcard">
              {selected.actions.length === 0 ? (
                <div className="m-formrow">
                  <label>{t('mobile.uld.actions', 'Delta actions')}</label>
                  <div className="m-formrow-ro">
                    {t('mobile.uld.no_actions', 'No actions — this screen renders its core layout unchanged.')}
                  </div>
                </div>
              ) : (
                selected.actions.map((a, i) => (
                  <div className="m-field" key={i}>
                    <span className="m-k">{actionLabel(a.action)}</span>
                    <span className="m-v">
                      {a.component_key}
                      {a.value ? <span className="m-formrow-hint"> · {a.value}</span> : null}
                    </span>
                  </div>
                ))
              )}
              <div className="m-formrow">
                <div className="m-formrow-ro">
                  <span className="m-formrow-hint">{t('mobile.uld.edit_desktop', 'Edit the delta on desktop')}</span>
                </div>
              </div>
            </div>

            {/* Collateral-damage telemetry — stale component_keys the current
                release no longer ships (the merger omits them silently). */}
            {selectedStale.length > 0 && (
              <div className="m-fieldcard">
                <div className="m-formrow">
                  <label>
                    <TriangleAlert size={13} style={{ verticalAlign: '-2px' }} /> {t('mobile.uld.stale_warnings', 'Stale-key warnings')}
                  </label>
                  <div className="m-formrow-ro">
                    <span className="m-formrow-hint">
                      {t('mobile.uld.stale_hint', 'Actions whose component_key the current release no longer ships.')}
                    </span>
                  </div>
                </div>
                {selectedStale.map((w) => (
                  <div className="m-field" key={w.id}>
                    <span className="m-k">{w.component_key}</span>
                    <span className="m-v">
                      {actionLabel(w.action_type)}
                      <span className="m-formrow-hint"> · {warningsLabel(w.occurrence_count)} · {formatDateTime(w.last_seen_at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
